import DOMPurify from "dompurify";

/**
 * Minimal markdown → sanitized HTML renderer.
 *
 * In-house implementace pro consent text body (spec-proposal 2026-05-24-01
 * DEC-013 G). Záměrně covers JEN podmnožinu markdown features, které consent
 * text reálně potřebuje:
 *
 *   - Headings h2/h3 (# / ## odřezány — top h1 je vždy "Souhlas se zpracováním…" v Stepperu)
 *   - Paragraphs (oddělené prázdným řádkem)
 *   - Unordered lists (- / * prefix)
 *   - **bold** a *italic* / _italic_
 *   - `inline code`
 *   - Plain links [text](url) — http(s) only
 *
 * Co NENÍ podporováno (consent text to nepotřebuje):
 *   - Ordered lists, nested lists, tabulky, blockquotes, code fences, images,
 *     HTML pass-through, footnotes
 *
 * Output prochází DOMPurify sanitization (defense-in-depth proti XSS přes
 * pasted obsah od admina). Pokud feature mimo pokrytí vznikne, je vždy
 * možnost vyměnit za `marked` po vyřešení pnpm store konfliktu — interface
 * této funkce zůstane stejný.
 */
export function renderConsentMarkdown(md: string): string {
  if (!md.trim()) return "";

  const escaped = escapeHtml(md);
  const blocks = escaped.split(/\n{2,}/);
  const htmlBlocks = blocks.map((block) => renderBlock(block.trim())).filter(Boolean);
  const html = htmlBlocks.join("\n");

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["h1", "h2", "h3", "h4", "p", "ul", "li", "strong", "em", "code", "a", "br"],
    ALLOWED_ATTR: ["href", "rel", "target"],
    ALLOW_DATA_ATTR: false,
  });
}

function renderBlock(block: string): string {
  if (!block) return "";

  // Heading: # / ## / ### / ####
  const headingMatch = block.match(/^(#{1,4})\s+(.+)$/);
  if (headingMatch && !block.includes("\n")) {
    const level = headingMatch[1]!.length;
    return `<h${level}>${renderInline(headingMatch[2]!)}</h${level}>`;
  }

  // List: lines start with - or *
  const lines = block.split(/\n/);
  if (lines.every((l) => /^[-*]\s+/.test(l))) {
    const items = lines
      .map((l) => `<li>${renderInline(l.replace(/^[-*]\s+/, ""))}</li>`)
      .join("");
    return `<ul>${items}</ul>`;
  }

  // Paragraph (single \n in block becomes <br>).
  const para = lines.map((l) => renderInline(l)).join("<br>");
  return `<p>${para}</p>`;
}

function renderInline(text: string): string {
  // Inline order matters: links first (může obsahovat **), pak bold/italic, pak code.
  // Po escapeHtml() je vstup safe — regex pracuje s entitami.
  let out = text;

  // Links [text](http(s)://...)
  out = out.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_m, label: string, url: string) =>
      `<a href="${url}" rel="noopener noreferrer" target="_blank">${label}</a>`,
  );

  // Bold **x**
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  // Italic *x* nebo _x_ (ne uvnitř slov; jednoduchý greedy ne-** match)
  out = out.replace(/(?<![*\w])\*([^*\n]+)\*(?![*\w])/g, "<em>$1</em>");
  out = out.replace(/(?<![_\w])_([^_\n]+)_(?![_\w])/g, "<em>$1</em>");

  // Inline code `x`
  out = out.replace(/`([^`\n]+)`/g, "<code>$1</code>");

  return out;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
